import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { CoursePicker } from 'components/admin/CoursePicker';
import { Button } from 'components/ui/button';
import { CardTitle } from 'components/ui/card';
import { Label } from 'components/ui/label';
import { useToast } from 'components/ui/use-toast';
import { exportCourseCsv, courseService } from 'services/api';
import type { Course } from 'types';

const sanitizeFilename = (value: string) =>
  value.replace(/[^A-Za-z0-9._-]+/g, '_').replace(/^_+|_+$/g, '');

const ContentExport = ({ active = true }: { active?: boolean }) => {
  const { addToast } = useToast();
  const [courses, setCourses] = useState<Course[]>([]);
  const [selectedCourseId, setSelectedCourseId] = useState<string>('');
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState('');
  const [repairPrerequisiteId, setRepairPrerequisiteId] = useState<number | null>(null);

  const selectedCourse = useMemo(
    () => courses.find((course) => String(course.id) === selectedCourseId),
    [courses, selectedCourseId],
  );

  const loadCourses = useCallback(async () => {
    setLoadingCourses(true);
    try {
      const response = await courseService.getAll();
      setCourses(response.data);
    } catch (error) {
      console.error('Failed to load courses', error);
      addToast({
        title: 'Unable to load courses',
        description: 'Please refresh the page or try again later.',
        variant: 'destructive',
      });
    } finally {
      setLoadingCourses(false);
    }
  }, [addToast]);

  const courseOptions = useMemo(
    () =>
      courses.map((course) => ({
        value: String(course.id),
        label: course.name,
        searchText: `${course.name} ${course.description ?? ''}`.trim(),
      })),
    [courses],
  );

  const courseEmptyMessage = loadingCourses ? 'Loading courses...' : 'No courses available';

  useEffect(() => {
    if (active) loadCourses();
  }, [active, loadCourses]);

  const handleExport = async () => {
    if (!selectedCourseId) {
      addToast({
        title: 'Select a course',
        description: 'Choose a course to export.',
        variant: 'destructive',
      });
      return;
    }

    setExportError('');
    setRepairPrerequisiteId(null);
    setIsExporting(true);
    try {
      const response = await exportCourseCsv(Number(selectedCourseId));
      const blob = response.data instanceof Blob ? response.data : new Blob([response.data]);
      const courseName = selectedCourse?.name || 'course';
      const filename = `${sanitizeFilename(courseName) || 'course'}.csv`;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      const skipped = response.headers?.['x-export-skipped'];
      if (skipped && Number(skipped) > 0) {
        addToast({
          title: 'Export completed with skips',
          description: `${skipped} questions were skipped because their types are not supported by import.`,
          variant: 'destructive',
        });
      } else {
        addToast({
          title: 'Export ready',
          description: 'Your CSV download has started.',
        });
      }
    } catch (error) {
      console.error('Failed to export course', error);
      const failure = error as { response?: { data?: { error?: string; detail?: string; prerequisite_id?: number | null } }; message?: string };
      const message = failure.response?.data?.error || failure.response?.data?.detail || failure.message || 'Please try again later.';
      setExportError(message);
      const prerequisiteId = failure.response?.data?.prerequisite_id;
      setRepairPrerequisiteId(typeof prerequisiteId === 'number' && prerequisiteId > 0 ? prerequisiteId : null);
      addToast({ title: 'Export failed', description: message, variant: 'destructive' });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <CardTitle
        title="Course Export"
        subtitle="Download a CSV that matches the import format for a selected course."
        subtitleSize="big"
      />

      <div className="space-y-6 md:w-3/4">
        <div className="space-y-2">
          <Label htmlFor="content-export-course">Course</Label>
          <CoursePicker
            value={selectedCourseId}
            onValueChange={value => { setSelectedCourseId(value); setExportError(''); }}
            options={courseOptions}
            placeholder="Select a course"
            triggerId="content-export-course"
            loading={loadingCourses}
            emptyMessage={courseEmptyMessage}
          />
        </div>

        {exportError && <div role="alert" className="space-y-2 rounded-xl border border-destructive/30 bg-destructive/5 p-4 text-sm"><p className="font-medium">Export could not be completed</p><p>{exportError}</p><Link to={`/admin/content?tab=course-builder&cbCourseId=${repairPrerequisiteId ?? selectedCourseId}`} className="inline-block font-medium underline underline-offset-4">{repairPrerequisiteId ? 'Open prerequisite in Course Builder' : 'Open Course Builder'}</Link></div>}
        <div className="flex items-center gap-3">
          <Button type="button" onClick={handleExport} disabled={isExporting}>
            {isExporting ? 'Preparing export…' : 'Export CSV'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ContentExport;
